import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import './Navigation.css';

export const Navigation = () => {
  const { i18n } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isGerman = i18n.language === 'de';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <nav className="navigation">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_91f93426-ea10-46d2-8a07-bd20f6d5069b/artifacts/kzhyxucd_MPG.png" 
              alt="MPG Logo" 
              className="logo-image" 
            />
            <span className="logo-text">
              {isGerman ? 'Versandzentrum' : 'Shipping Center'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-links desktop-only">
            <LanguageSwitcher />
            
            {user ? (
              <>
                <Link to="/profile" className="nav-link" data-testid="profile-link">
                  {isGerman ? 'Profil' : 'Profile'}
                </Link>
                
                {isAdmin() && (
                  <Link to="/admin" className="nav-link admin-link" data-testid="admin-link">
                    {isGerman ? 'Admindashboard' : 'Admin Dashboard'}
                  </Link>
                )}
                
                <button onClick={handleLogout} className="nav-button logout" data-testid="logout-button">
                  {isGerman ? 'Abmelden' : 'Logout'}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link" data-testid="login-link">
                  {isGerman ? 'Anmelden' : 'Login'}
                </Link>
                <Link to="/register" className="nav-button primary" data-testid="register-link">
                  {isGerman ? 'Registrieren' : 'Register'}
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger Button */}
          <button 
            className="hamburger-button mobile-only" 
            onClick={toggleMobileMenu}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            <span className={mobileMenuOpen ? 'hamburger-icon open' : 'hamburger-icon'}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
        </div>
      </nav>

      {/* Mobile Off-Canvas Menu */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={toggleMobileMenu}></div>
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <h2>{isGerman ? 'Menü' : 'Menu'}</h2>
          <button onClick={toggleMobileMenu} className="close-button" aria-label="Close menu">
            ✕
          </button>
        </div>

        <div className="mobile-menu-content">
          <div className="mobile-menu-section">
            <LanguageSwitcher />
          </div>

          {user ? (
            <>
              <Link to="/profile" className="mobile-menu-item">
                <span className="menu-icon">👤</span>
                {isGerman ? 'Profil' : 'Profile'}
              </Link>
              
              {isAdmin() && (
                <Link to="/admin" className="mobile-menu-item admin">
                  <span className="menu-icon">👨‍💼</span>
                  {isGerman ? 'Admindashboard' : 'Admin Dashboard'}
                </Link>
              )}
              
              <button onClick={handleLogout} className="mobile-menu-item logout">
                <span className="menu-icon">🚪</span>
                {isGerman ? 'Abmelden' : 'Logout'}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="mobile-menu-item">
                <span className="menu-icon">🔐</span>
                {isGerman ? 'Anmelden' : 'Login'}
              </Link>
              <Link to="/register" className="mobile-menu-item primary">
                <span className="menu-icon">📝</span>
                {isGerman ? 'Registrieren' : 'Register'}
              </Link>
            </>
          )}

          <div className="mobile-menu-footer">
            <Link to="/privacy" className="mobile-menu-link">
              {isGerman ? 'Datenschutz' : 'Privacy'}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
